import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';

import { queryKeys } from '../../queryKeys';

import { AlertSettingsRow } from './types';

export interface ConnectivityTestResponse {
  reachable: boolean;
  message: string;
}

// Combined Alert Settings hook
export function useCombinedAlertSettings<T = AlertSettingsRow[]>({
  refetchInterval,
  select,
}: {
  refetchInterval?: number;
  select?: (data: AlertSettingsRow[]) => T;
} = {}) {
  return useQuery(
    queryKeys.settings(),
    async () => {
      const { data } = await axios.get('/observability/alerting/settings');
      return (data || []).map((setting: Record<string, unknown>) => ({
        ...setting,
        id: setting.id?.toString() || setting.ID?.toString(),
        isInternal: setting.isInternal ?? setting.IsInternal ?? false,
        name: setting.name || setting.Name || '',
        url: setting.url || setting.URL || '',
        enabled: setting.enabled ?? setting.Enabled ?? false,
        status: setting.status || setting.Status || 'disconnected',
        notificationChannels:
          setting.notificationChannels || setting.NotificationChannels || [],
        portainerURL: setting.portainerURL || setting.PortainerURL || '',
      }));
    },
    {
      refetchInterval,
      select,
      ...withError('Unable to get combined alert settings'),
    }
  );
}

// Update Alert Settings hook
export function useUpdateAlertSettings() {
  const queryClient = useQueryClient();

  return useMutation(
    async (alertSettings: AlertSettingsRow) => {
      const payload = {
        AlertingSettings: {
          ID: parseInt(alertSettings.id, 10),
          IsInternal: alertSettings.isInternal,
          Name: alertSettings.name,
          URL: alertSettings.url,
          Enabled: alertSettings.enabled,
          Status: alertSettings.status,
          PortainerURL: window.location.origin,
          NotificationChannels: (alertSettings.notificationChannels || []).map(
            (channel) => ({
              ID: parseInt(channel.id, 10),
              Name: channel.name,
              Type: channel.type,
              Enabled: channel.enabled ?? true,
              Config: channel.config,
            })
          ),
        },
      };

      const { data } = await axios.put(
        '/observability/alerting/settings',
        payload
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.settings());
      },
      ...withError('Failed to update alert settings'),
    }
  );
}

// Test Alert Manager Connectivity hook
export function useTestAlertManagerConnectivity() {
  return useMutation(async (url: string): Promise<ConnectivityTestResponse> => {
    try {
      const { data } = await axios.get<ConnectivityTestResponse>(
        '/observability/alerting/connectivity',
        {
          params: { url },
        }
      );
      return data;
    } catch {
      return {
        reachable: false,
        message: 'Failed to test connectivity',
      };
    }
  });
}
